import {
  useQuery,
  UseQueryResult,
  keepPreviousData,
} from '@tanstack/react-query'
import {
  ArrayLocationAPIResponse,
  SiteRFPSummariesResponseBody,
  SiteRFPSummariesSortByParam,
  SiteRFPSummary,
} from '@black-bear-energy/black-bear-energy-common'
import { AxiosInstance } from 'axios'
import { useApiWithIDTokenGranted } from '../../state/modules/common'
import { transformSiteRFPSummary } from './helpers/transformSiteRFPSummary'
import { SiteRFPPhaseOption } from 'components/helpers/getSiteRFPPhaseLabel'

export type SiteActiveStatus = 'active' | 'dropped'

export type SiteRFPSummariesResult = {
  records: SiteRFPSummary[]
  pagination: SiteRFPSummariesResponseBody['pagination']
}

/**
 * Fetch paginated site RFP summaries for populating the Sites landing page dashboard.
 * @param search - The search term to filter by. This can be a project or site name.
 * @param activeStatus - Whether to only show sites that are 'active' or 'dropped'.
 * @param phase - The site RFP phases to filter by.
 * @param arrayLocation - The site RFP array locations to filter by.
 * @param sortBy - The field to sort by.
 * @param sortAsc - If false, the results will be sorted in descending order.
 * @param page - The page number to fetch, starting at 1.
 * @returns A UseQueryResult object containing the site RFP summaries and pagination information.
 */
export const useSiteRFPSummaries = (
  search: string = '',
  activeStatus: SiteActiveStatus = 'active',
  phase: SiteRFPPhaseOption[] = [],
  arrayLocation: ArrayLocationAPIResponse[] = [],
  sortBy: SiteRFPSummariesSortByParam = SiteRFPSummariesSortByParam.SiteName,
  sortAsc: boolean = true,
  page: number = 1
): UseQueryResult<SiteRFPSummariesResult, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: [
      api,
      'siteRFPSummaries',
      search,
      activeStatus,
      phase,
      arrayLocation,
      sortBy,
      sortAsc,
      page,
    ],
    queryFn: () =>
      fetchSiteRFPSummaries(
        api as AxiosInstance,
        search,
        activeStatus,
        phase,
        arrayLocation,
        sortBy,
        sortAsc,
        page
      ),
    select: transformSiteRFPSummaries,
    placeholderData: keepPreviousData,
    enabled: !!api,
  })
}

async function fetchSiteRFPSummaries(
  api: AxiosInstance,
  search: string,
  activeStatus: SiteActiveStatus,
  phase: SiteRFPPhaseOption[],
  arrayLocation: ArrayLocationAPIResponse[],
  sortBy: SiteRFPSummariesSortByParam,
  sortAsc: boolean,
  page: number
): Promise<SiteRFPSummariesResponseBody> {
  const response = await api.get<SiteRFPSummariesResponseBody>(
    '/site-rfps/site-rfp-summaries',
    {
      params: {
        search,
        activeStatus,
        phase,
        arrayLocation,
        sortBy,
        sortAsc,
        page,
      },
    }
  )
  return response.data
}

const transformSiteRFPSummaries = (
  data: SiteRFPSummariesResponseBody
): SiteRFPSummariesResult => ({
  records: data.records.map(transformSiteRFPSummary),
  pagination: data.pagination,
})
